import {
  Card,
  MenuItem,
  Select,
  Box,
  InputLabel,
  Button,
} from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import {
  ClimateCondition,
  SoilCondition,
  Condition,
  LoggerCondition,
  ProbeOfflineCondition,
} from "../../../api/automation";

import { MetricConditionInput } from "./inputs/MetricCondition";
import { SoilClimateInput } from "./inputs/SoilCondition";
import { TimeConditionInput } from "./inputs/TimeCondition";
import { ConditionChart } from "./ConditionChart";
import { ClimateMetric, SoilMetric } from "../../../constants/metrics";
import { Field } from "../../../api/fields";
import { UserState } from "../../../context/auth";
import { LoggerConditionInput } from "./inputs/LoggerCondition";
import { ProbeOfflineConditionInput } from "./inputs/ProbeOfflineCondition";
import { Logger } from "../../../api/loggers";

const ClimateConditionInput = MetricConditionInput<ClimateCondition>(
  "climate",
  [
    "temperature",
    "humidity",
    "vpd",
    "delta_t",
    "light",
    "wind_speed",
    "wind_direction",
  ]
);
const SoilConditionInput = MetricConditionInput<SoilCondition>("soil", [
  "soil_temp",
  "vic",
  "gains",
  "run_off",
  "rainfall",
  "ph_in",
  "ph_out",
  "ph_bag",
  "ec_in",
  "ec_out",
  "ec_sum",
  "ec_bag",
]);

const conditionChanged = (
  oldCondition: Condition | null,
  newCondition: Condition
) => {
  switch (oldCondition?.type) {
    case "climate":
    case "soil":
      return (
        newCondition.type !== oldCondition.type ||
        oldCondition.metric !== newCondition.metric ||
        oldCondition.comparison !== newCondition.comparison ||
        oldCondition.threshold !== newCondition.threshold
      );
    case "soil_climate":
      return (
        newCondition.type !== "soil_climate" ||
        oldCondition.threshold_id !== newCondition.threshold_id ||
        oldCondition.threshold_manual !== newCondition.threshold_manual ||
        oldCondition.threshold_name !== newCondition.threshold_name ||
        oldCondition.comparison !== newCondition.comparison
      );
    case "time":
      return (
        newCondition.type !== "time" ||
        oldCondition.start !== newCondition.start ||
        oldCondition.end !== newCondition.end
      );
    case "logger":
      return (
        newCondition.type !== "logger" ||
        oldCondition.metric !== newCondition.metric ||
        oldCondition.comparison !== newCondition.comparison ||
        oldCondition.threshold !== newCondition.threshold ||
        oldCondition.logger_id !== newCondition.logger_id
      );
    case "probe_offline":
      return (
        newCondition.type !== "probe_offline" ||
        oldCondition.threshold_minutes !== newCondition.threshold_minutes
      );
    case undefined:
      return true;
  }
};

const conditionLabels: Record<Condition["type"], string> = {
  climate: "Climate",
  soil: "Root Zone",
  soil_climate: "Root Zone Moisture",
  time: "Time",
  logger: "Logger Status",
  probe_offline: "Probe Offline",
  or: "Or",
  and: "And",
};

export const ConditionCard: React.FC<{
  value: Condition | null;
  user: UserState;
  fieldId: number | undefined;
  field?: Field;
  loggers?: Logger[];
  onChange: (c: Condition | null) => void;
  onRemove: () => void;
}> = ({ value, user, fieldId, field, loggers, onChange, onRemove }) => {
  const [type, setType] = useState<Condition["type"] | undefined>(value?.type);
  const typeOptions = useMemo(() => {
    const result: Condition["type"][] = [
      "climate",
      "soil",
      "soil_climate",
      "probe_offline",
    ];
    if (loggers?.length) {
      result.push("logger");
    }
    return result;
  }, [loggers, fieldId]);

  const [result, setResult] = useState(value);

  useEffect(() => {
    setType(value?.type);
    setResult(value);
  }, [value]);
  const [selectedMetric, setSelectedMetric] = useState<
    SoilMetric | ClimateMetric
  >();
  const info = useMemo(() => {
    if (!user.token || !user.client.id) return;
    return {
      token: user.token,
      clientId: user.client.id,
      fieldId,
      loggers,
    };
  }, [user, fieldId, loggers]);

  useEffect(() => {
    if (type === "soil_climate") {
      setSelectedMetric("moisture_content");
    } else if (type !== "climate" && type !== "soil") {
      // Types without an associated metric (probe_offline, logger, time)
      // shouldn't render a chart — clear any stale metric from a previous type.
      setSelectedMetric(undefined);
    }
  }, [type]);

  if (!info) return null;

  return (
    <Box m={2}>
      <Card style={{ display: "flex", flexDirection: "row" }}>
        <Box p={2} width={fieldId && selectedMetric ? "45%" : "100%"}>
          {type !== "time" && (
            <>
              <InputLabel id="type" shrink>
                Type
              </InputLabel>
              <Box
                flexDirection="row"
                display="flex"
                justifyContent="space-between"
              >
                <Select
                  label="type"
                  value={type ?? ""}
                  onChange={(e) => {
                    setType(e.target.value as Condition["type"]);
                    setResult(null);
                  }}
                >
                  {typeOptions.map((t) => (
                    <MenuItem value={t} key={t}>
                      {conditionLabels[t]}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </>
          )}
          <Box mt={1}>
            {type === "climate" && (
              <ClimateConditionInput
                value={{ ...(value as ClimateCondition), type: "climate" }}
                info={info}
                onChange={setResult}
                field={field}
                onSelectMetric={(value) => {
                  setSelectedMetric(value);
                }}
              />
            )}
            {type === "soil" && (
              <SoilConditionInput
                value={{ ...(value as SoilCondition), type: "soil" }}
                info={info}
                onChange={setResult}
                field={field}
                onSelectMetric={(value) => {
                  setSelectedMetric(value);
                }}
              />
            )}
            {type === "soil_climate" && (
              <SoilClimateInput
                value={{ ...value, type: "soil_climate" }}
                info={info}
                onChange={setResult}
                field={field}
                onSelectMetric={(value) => {
                  setSelectedMetric(value);
                }}
              />
            )}
            {type === "logger" && (
              <LoggerConditionInput
                value={{ ...(value as LoggerCondition), type: "logger" }}
                info={info}
                onChange={setResult}
              />
            )}
            {type === "probe_offline" && (
              <ProbeOfflineConditionInput
                value={{
                  ...(value as ProbeOfflineCondition),
                  type: "probe_offline",
                }}
                info={info}
                onChange={setResult}
              />
            )}
            {type === "time" && (
              <TimeConditionInput
                value={{ ...value, type: "time" }}
                info={info}
                onChange={setResult}
              />
            )}
          </Box>
          <Box mt={3}>
            <Button
              disabled={result == null || !conditionChanged(value, result)}
              onClick={() => onChange(result)}
              variant="contained"
              color="primary"
            >
              {value === undefined ? "Add" : "Update"}
            </Button>
            {type !== "time" && (
              <Button
                onClick={onRemove}
                variant="contained"
                style={{
                  background: "lightgrey",
                  marginLeft: 8,
                }}
              >
                Remove
              </Button>
            )}
          </Box>
        </Box>
        {fieldId && type && type !== "time" && selectedMetric && (
          <Box width={"45%"} height={250}>
            <ConditionChart field={field} metric={selectedMetric} />
          </Box>
        )}
      </Card>
    </Box>
  );
};
